import React, { useEffect, useState } from "react";
import { Lock, Anchor } from "lucide-react";
import { getIpadCoverPath } from "@/lib/guest";
import { fileUrl } from "@/lib/api";

/**
 * Global overlay rendered whenever any /public/ipad/* endpoint replies 423
 * Locked — meaning the cabin's occupancy_status has switched to "crew" or
 * "not_available". The axios interceptor in `api.js` dispatches a
 * `cabin-locked` custom event with the polite message from the backend;
 * this component listens for it and paints a full-screen card OVER the
 * cabin's cover photo so the guest still recognises which cabin they're in.
 * If no cover photo exists we fall back to a deep-navy vignette.
 *
 * Reappears on the next 423 poll if dismissed, and clears automatically
 * when a subsequent /public/ipad/* call succeeds (cabin unlocked).
 */
export default function CabinLockedOverlay() {
  const [state, setState] = useState({ open: false, message: "" });
  const [coverPath, setCoverPath] = useState(getIpadCoverPath());

  useEffect(() => {
    const onLocked = (e) => {
      const message = (e.detail && e.detail.message) || "Bookings for this cabin are unavailable at the moment.";
      // Refresh the cover path each time we lock — the iPad session may have
      // rotated cabins between locks in edge cases.
      setCoverPath(getIpadCoverPath());
      setState({ open: true, message });
    };
    const onUnlocked = () => setState({ open: false, message: "" });
    const onSessionChanged = () => setCoverPath(getIpadCoverPath());
    window.addEventListener("cabin-locked", onLocked);
    window.addEventListener("cabin-unlocked", onUnlocked);
    window.addEventListener("ipad-session-changed", onSessionChanged);
    return () => {
      window.removeEventListener("cabin-locked", onLocked);
      window.removeEventListener("cabin-unlocked", onUnlocked);
      window.removeEventListener("ipad-session-changed", onSessionChanged);
    };
  }, []);

  if (!state.open) return null;

  // Background: cabin cover photo when we have one (so guests still recognise
  // the room), otherwise a deep navy fallback. Either way we lay a strong
  // dark tint on top so the copy stays legible.
  const bgStyle = coverPath
    ? {
        backgroundImage: `linear-gradient(180deg, rgba(6, 14, 26, 0.55) 0%, rgba(6, 14, 26, 0.9) 100%), url("${fileUrl(coverPath)}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : { backgroundColor: "rgba(6, 14, 26, 0.94)" };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-6 backdrop-blur-md"
      style={bgStyle}
      data-testid="cabin-locked-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-xl w-full text-center">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-white/40 mb-6 bg-black/25" aria-hidden>
          <Lock className="h-7 w-7 text-white" strokeWidth={1.4} />
        </div>
        <div className="overline text-[10px] tracking-[0.3em] text-white/70">Yacht Spa · Temporarily Unavailable</div>
        <h2 className="font-display text-3xl sm:text-4xl text-white mt-3 leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
          A quiet interlude
        </h2>
        <p className="text-white/95 mt-5 text-base sm:text-lg leading-relaxed drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]" data-testid="cabin-locked-message">
          {state.message}
        </p>
        <div className="mt-8 flex items-center justify-center gap-2 text-white/60 text-[11px] tracking-widest uppercase">
          <Anchor className="h-3 w-3" strokeWidth={1.5} />
          Please check with the crew · we'll be right back
        </div>
      </div>
    </div>
  );
}
